import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Image, Typography, Pagination, LoadingIndicator } from 'lib/components';
import { useAppDispatch, actions, useSelectCustomCosts, useSelectRequest, RequestManager } from 'store';
import { useCustomToast, usePagination } from 'hooks';
import { CustomCost } from 'types';
import { routes } from 'lib/api';
import classes from './index.module.scss';
import { RateType } from '../../lib/util';

/**
 * Props interface for the TableRow component
 */
interface TableRowProps {
	/** Custom cost object containing cost amplifier data */
	cost: CustomCost;
	/** Callback function when delete button is clicked */
	onDelete: (id: number) => void;
}

/**
 * TableHeader component
 * Renders the header row for the cost amplifiers table
 * Displays column titles for cost name and different rate types
 *
 * @returns {JSX.Element} - Rendered component
 */
const TableHeader = () => (
	<div className={classes['costAmplifierTab-tableHeader']}>
		{/* Cost amplifier name column */}
		<div>
			<Typography color="darkgrey" variant="h6">
				Title
			</Typography>
		</div>
		{/* Business day rate column */}
		<div>
			<Typography color="darkgrey" variant="h6">
				Business Day
			</Typography>
		</div>
		{/* Overtime rate column */}
		<div>
			<Typography color="darkgrey" variant="h6">
				Overtime
			</Typography>
		</div>
		{/* Weekend rate column */}
		<div>
			<Typography color="darkgrey" variant="h6">
				Weekend
			</Typography>
		</div>
		{/* Holiday rate column */}
		<div>
			<Typography color="darkgrey" variant="h6">
				Holiday
			</Typography>
		</div>
	</div>
);

/**
 * TableRow component
 * Renders a row in the cost amplifiers table for a single cost amplifier
 * Displays cost name, rates for different time periods, and action buttons (edit/delete)
 *
 * @param {TableRowProps} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const TableRow = ({ cost, onDelete }: TableRowProps) => {
	// Navigation helper
	const { push } = useHistory();

	/**
	 * Navigate to the edit cost amplifier page for this cost
	 * Memoized with useCallback to prevent unnecessary re-renders
	 */
	const navToEditCostAmp = useCallback(() => {
		push(`/settings/cost-amplifier/edit/${cost.id}`);
	}, [cost]);

	/**
	 * Format rate value based on rate type (fixed or percentage)
	 * Fixed rates are displayed with $ symbol and 2 decimal places
	 * Percentage rates are displayed with % symbol and 0 decimal places
	 *
	 * @param {number} value - The rate value
	 * @param {RateType} rateType - The type of rate (fixed or percentage)
	 * @returns {string} - Formatted rate string
	 */
	const getValue = useCallback((value: number, rateType: RateType) => {
		if (rateType === RateType.FIXED) {
			return `$${value.toFixed(2)}`;
		}

		return `${value.toFixed(0)}%`;
	}, []);

	return (
		<div className={classes['costAmplifierTab-tableRow']}>
			{/* Cost amplifier name column */}
			<div>
				<Typography variant="h6">{cost.name}</Typography>
			</div>
			{/* Business day rate column */}
			<div>
				<Typography color="primary" variant="h6">
					{getValue(cost.regularRate, cost.regularRateTypeId)}
				</Typography>
			</div>
			{/* Overtime rate column */}
			<div>
				<Typography color="primary" variant="h6">
					{getValue(cost.overtimeRate, cost.overtimeRateTypeId)}
				</Typography>
			</div>
			{/* Weekend rate column */}
			<div>
				<Typography color="primary" variant="h6">
					{getValue(cost.weekendRate, cost.weekendRateTypeId)}
				</Typography>
			</div>
			{/* Holiday rate column */}
			<div>
				<Typography color="primary" variant="h6">
					{getValue(cost.holidayRate, cost.holidayRateTypeId)}
				</Typography>
			</div>
			{/* Action buttons column */}
			<div className={classes['costAmplifierTab-tableRowActions']}>
				{/* Edit button */}
				<Image src="icEdit" onClick={navToEditCostAmp} />
				{/* Delete button */}
				<Image src="icTrashDanger" onClick={() => onDelete(cost.id)} />
			</div>
		</div>
	);
};

/**
 * CostAmplifierTab component
 * Displays and manages custom cost amplifiers in the settings screen
 * Allows viewing, adding, editing, and deleting cost amplifiers
 *
 * @returns {JSX.Element} - Rendered component
 */
const CostAmplifierTab = () => {
	// Custom toast notifications for user feedback
	const toast = useCustomToast();
	// Navigation helper
	const { push } = useHistory();

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Active API request tracking
	const { active } = useSelectRequest();
	// Custom costs data from Redux store
	const costs = useSelectCustomCosts();

	// ID of the cost being deleted, -1 means no deletion in progress
	const [toDelete, setToDelete] = useState(-1);

	/**
	 * Fetch custom costs data on component mount
	 */
	useEffect(() => {
		dispatch(actions.fetchCustomCosts());
	}, []);

	/**
	 * Set up pagination for custom costs list
	 * Returns data and handlers for paginated display with 8 items per page
	 */
	const { isDataFetched, listing, paginatedData, paginationProps } = usePagination(costs, { itemsPerPage: 8 });

	/**
	 * Monitor API requests for cost deletion
	 * Shows toast notifications for errors and resets deletion state on success
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		if (toDelete !== -1 && RM.req(routes.CUSTOM_COST_ID(toDelete), 'DELETE')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				// Reset deletion state after successful deletion
				setToDelete(-1);
			}
		}
	}, [active]);

	/**
	 * Navigate to the add cost amplifier page
	 * Memoized with useCallback to prevent unnecessary re-renders
	 */
	const navToAddCostAmp = useCallback(() => {
		push('/settings/cost-amplifier/add');
	}, []);

	/**
	 * Handle cost amplifier deletion
	 * Sets the cost ID to delete and dispatches the delete action
	 *
	 * @param {number} id - ID of the cost amplifier to delete
	 */
	const handleDelete = (id: number) => {
		setToDelete(id);
		dispatch(actions.deleteCustomCost(id));
	};

	// Show loading indicator while data is being fetched
	if (!isDataFetched) return <LoadingIndicator />;

	return (
		<>
			<div className={classes['costAmplifierTab']}>
				{/* Cost amplifiers table container */}
				<div className={classes['costAmplifierTab-table']}>
					{/* Only show table header if there are cost amplifiers to display */}
					{listing.length > 0 && <TableHeader />}
					{/* Table body with cost amplifier rows */}
					<div className={classes['costAmplifierTab-tableBody']}>
						{/* Map through paginated cost amplifiers and render a row for each */}
						{paginatedData.map((cost) => (
							<TableRow key={cost.id} cost={cost} onDelete={handleDelete} />
						))}
					</div>
					{/* Table footer with add button */}
					<div className={classes['costAmplifierTab-tableFooter']}>
						<a onClick={navToAddCostAmp} className={classes['costAmplifierTab-tableFooterButton']}>
							+ Add New Custom Cost Amplifier
						</a>
					</div>
				</div>
			</div>
			{/* Pagination controls */}
			<Pagination {...paginationProps} />
		</>
	);
};

export default CostAmplifierTab;
